import React, { useMemo } from "react";
import LazyLoad from "react-lazy-load";
import { connect } from "react-redux";
import useQueryHook from "utils/useQueryHook";

import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  flexRender,
} from "@tanstack/react-table";

import ProgressStatus from "../ProgressStatus";
import Events from "../Events";
import ProgressBar from "./components/ProgressBar";
import MainFilters from "./components/MainFilters";
import Avatar from "./components/Avatar";
import StartWorkflow from "../StartWorkflow";

import {
  getRootTaxonLabel,
  getHeadStyle,
  getRowStyle,
  getCellStyle,
} from "./helpers";

function GroupedProductsList({
  current_user,
  progress_status,
  refetchStatuses,
  is_mass_event_enabled,
  mass_events,
  setMassEvents,
  query_filters,
  refetch,
  load_more_ref,
  handleSetQueryFilters,
  data,
  meta,
  fetchNextPage,
  is_fetching_next_page,
  is_refetching,
}) {
  const {
    data: { data: root_taxons },
  } = useQueryHook({
    query_keys: ["root_taxons"],
    path: `/root_taxons/tree`,
  });

  const {
    data: { data: platforms },
  } = useQueryHook({
    query_keys: ["platforms"],
    path: `/platforms?context=list&per_page=10000`,
  });

  const {
    data: { data: shops },
  } = useQueryHook({
    query_keys: ["shops"],
    path: `/shops?context=list&per_page=10000`,
  });

  const {
    data: { data: channels },
  } = useQueryHook({
    query_keys: ["channels"],
    path: `/channels?context=list&per_page=10000`,
  });

  const {
    data: { data: workers },
  } = useQueryHook({
    query_keys: ["workers"],
    path: `/users?context=list&per_page=200&q[is_active_eq]=true&q[permission_workflow_worker_view_eq]=true`,
  });

  const [expanded, setExpanded] = React.useState({});

  const columns = React.useMemo(
    () => [
      {
        id: "id",
        header: () => (
          <>
            <span>SKU</span>
            <p className="extra">Nazwa produktu</p>
          </>
        ),
        cell: ({ row }) => (
          <div
            style={{
              paddingLeft: `${row.depth * 20}px`,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            {row.getCanExpand() && (
              <svg
                width="10"
                height="6"
                viewBox="0 0 10 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  transform: !row.getIsExpanded()
                    ? "rotate(-90deg)"
                    : "rotate(0)",
                }}
              >
                <path
                  fill="none"
                  d="M9 1L5 5L1 1"
                  stroke="#192027"
                  strokeLinecap="round"
                />
              </svg>
            )}
            <div className="product-data">
              {row.depth === 0 && (
                <>
                  <div className="product-data__info">
                    <p data-tip={row?.original?.name}>{row?.original?.name}</p>
                    <div className="df" style={{ gap: 5 }}>
                      <strong>
                        <a
                          onClick={(e) => e.stopPropagation()}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-link"
                          href={`/products_manage/product_groups/${row?.original?.product_group_id}/specification/properties`}
                        >
                          {row?.original?.sku}
                        </a>
                      </strong>
                      <strong>|</strong>
                      <strong>{row?.original?.brand_name}</strong>
                      <strong>|</strong>
                      <strong>
                        K:{" "}
                        {
                          row.original
                            .products_with_specification_not_done_count
                        }
                      </strong>
                      <strong>|</strong>
                      <strong>
                        G: {row.original.products_with_image_not_done_count}
                      </strong>
                    </div>
                  </div>
                  {current_user.permission_workflow_admin_view && (
                    <div className="product-data__assigners">
                      {row.original.product_group_assigns?.map(
                        ({ id, user }) => (
                          <Avatar
                            workers={workers}
                            key={id}
                            type="edit"
                            id={id}
                            user={user}
                            product_group_name={row?.original?.name}
                            refetch={() =>
                              refetch(row?.original?.product_group_id)
                            }
                          />
                        ),
                      )}
                      <Avatar
                        workers={workers}
                        type="add"
                        product_group_name={row?.original?.name}
                        product_group_id={row?.original?.product_group_id}
                        refetch={() => refetch(row?.original?.product_group_id)}
                      />
                    </div>
                  )}
                </>
              )}
              {row.depth === 1 && row?.original?.type === "Product" && (
                <>
                  <div className="product-data__image">
                    {row?.original?.global_image_thumb_url && (
                      <LazyLoad height={30} width={30}>
                        <img
                          src={row?.original?.global_image_thumb_url}
                          alt={row?.original?.name}
                        />
                      </LazyLoad>
                    )}
                  </div>
                  <div className="product-data__info">
                    <p>{row?.original?.name}</p>
                    <p>
                      <strong>{row?.original?.sku}</strong>
                    </p>
                  </div>
                </>
              )}
              {row.depth === 1 &&
                row?.original?.type === "ChannelProductGroup" && (
                  <div className="product-data__info">
                    <p>{row?.original?.channel_identifier}</p>
                  </div>
                )}
              {row.depth === 2 && (
                <div className="product-data__info">
                  <p>{row.original?.channel_identifier}</p>
                </div>
              )}
            </div>
          </div>
        ),
      },
      {
        id: "progress_status",
        header: () => <span>Status</span>,
        cell: ({ row }) =>
          progress_status === "pending" ? (
            <StartWorkflow
              is_mass_event_enabled={is_mass_event_enabled}
              mass_events={mass_events}
              setMassEvents={setMassEvents}
              type={row?.original?.type}
              id={row?.original?.id}
              refetch={() => refetch(row?.original?.product_group_id)}
              refetchStatuses={refetchStatuses}
            />
          ) : (
            row.depth === 2 && (
              <p className="text-muted">
                <ProgressStatus status={row.original.progress_status} />
              </p>
            )
          ),
      },
      {
        id: "specification_product_group_workflow",
        header: () => (
          <>
            <span>Specyfikacja</span>
            <p className="extra">produkt grupowy</p>
          </>
        ),
        cell: ({ row }) =>
          row.depth === 0 && (
            <Events
              is_mass_event_enabled={is_mass_event_enabled}
              mass_events={mass_events}
              setMassEvents={setMassEvents}
              refetch={() => refetch(row?.original?.product_group_id)}
              refetchStatuses={refetchStatuses}
              available_workflow="specification_product_group_workflow"
              available_events={row?.original?.available_events}
              id={row?.original?.id}
              type={row.original?.type}
              status={row?.original?.specification_status}
            />
          ),
      },
      {
        id: "specification_product_workflow",
        header: () => (
          <>
            <span>Specyfikacja</span>
            <p className="extra">produkt</p>
          </>
        ),
        cell: ({ row }) => (
          <>
            {row.depth === 0 && (
              <ProgressBar
                data={row.original?.specification_product_statuses}
              />
            )}
            {row.depth === 1 && row?.original?.type === "Product" && (
              <Events
                is_mass_event_enabled={is_mass_event_enabled}
                mass_events={mass_events}
                setMassEvents={setMassEvents}
                refetch={() => refetch(row?.original?.product_group_id)}
                refetchStatuses={refetchStatuses}
                available_workflow="specification_product_workflow"
                available_events={row.original?.available_events}
                id={row.original?.id}
                type={row.original?.type}
                status={row.original?.specification_status}
              />
            )}
          </>
        ),
      },
      {
        id: "image_workflow",
        header: () => (
          <>
            <span>Grafiki</span>
            <p className="extra">produkt</p>
          </>
        ),
        cell: ({ row }) => (
          <>
            {row.depth === 0 && (
              <ProgressBar data={row.original?.image_statuses} />
            )}
            {row.depth === 1 && row?.original?.type === "Product" && (
              <Events
                is_mass_event_enabled={is_mass_event_enabled}
                mass_events={mass_events}
                setMassEvents={setMassEvents}
                refetch={() => refetch(row?.original?.product_group_id)}
                refetchStatuses={refetchStatuses}
                available_workflow="image_workflow"
                available_events={row.original?.available_events}
                id={row.original?.id}
                type={row.original?.type}
                status={row.original?.image_status}
              />
            )}
          </>
        ),
      },
      {
        id: "category_workflow",
        header: () => (
          <>
            <span>Kategoria</span>
            <p className="extra">produkt grupowy na kanale</p>
          </>
        ),
        cell: ({ row }) => (
          <>
            {row.depth === 0 && (
              <ProgressBar data={row.original?.category_statuses} />
            )}
            {row.depth === 1 &&
              row?.original?.type === "ChannelProductGroup" && (
                <Events
                  is_mass_event_enabled={is_mass_event_enabled}
                  mass_events={mass_events}
                  setMassEvents={setMassEvents}
                  refetch={() => refetch(row?.original?.product_group_id)}
                  refetchStatuses={refetchStatuses}
                  available_workflow="category_workflow"
                  available_events={row.original?.available_events}
                  id={row.original?.id}
                  type={row.original?.type}
                  status={row.original?.category_status}
                />
              )}
          </>
        ),
      },
      {
        id: "description_workflow",
        header: () => (
          <>
            <span>Opisy</span>
            <p className="extra">produkt grupowy na kanale</p>
          </>
        ),
        cell: ({ row }) => (
          <>
            {row.depth === 0 && (
              <ProgressBar data={row.original?.description_statuses} />
            )}
            {row.depth === 1 &&
              row?.original?.type === "ChannelProductGroup" && (
                <Events
                  is_mass_event_enabled={is_mass_event_enabled}
                  mass_events={mass_events}
                  setMassEvents={setMassEvents}
                  refetch={() => refetch(row?.original?.product_group_id)}
                  refetchStatuses={refetchStatuses}
                  available_workflow="description_workflow"
                  available_events={row.original?.available_events}
                  id={row.original?.id}
                  type={row.original?.type}
                  status={row.original?.description_status}
                />
              )}
          </>
        ),
      },
      {
        id: "title_workflow",
        header: () => (
          <>
            <span>Tytuły</span>
            <p className="extra">produkt na kanale</p>
          </>
        ),
        cell: ({ row }) => (
          <>
            {(row.depth === 0 || row.depth === 1) && (
              <ProgressBar data={row.original?.title_statuses} />
            )}
            {row.depth === 2 && (
              <Events
                is_mass_event_enabled={is_mass_event_enabled}
                mass_events={mass_events}
                setMassEvents={setMassEvents}
                refetch={() => refetch(row?.original?.product_group_id)}
                refetchStatuses={refetchStatuses}
                available_events={row.original?.available_events}
                id={row.original?.id}
                type={row.original?.type}
                available_workflow={["title_workflow"]}
                status={row.original?.title_status}
              />
            )}
          </>
        ),
      },
      {
        id: "image_assign_workflow",
        header: () => (
          <>
            <span>Przypisane zdjęcia</span>
            <p className="extra">produkt na kanale</p>
          </>
        ),
        cell: ({ row }) => (
          <>
            {(row.depth === 0 || row.depth === 1) && (
              <ProgressBar data={row.original?.image_assign_statuses} />
            )}
            {row.depth === 2 && (
              <Events
                is_mass_event_enabled={is_mass_event_enabled}
                mass_events={mass_events}
                setMassEvents={setMassEvents}
                refetch={() => refetch(row?.original?.product_group_id)}
                refetchStatuses={refetchStatuses}
                available_events={row.original?.available_events}
                id={row.original?.id}
                type={row.original?.type}
                available_workflow="image_assign_workflow"
                status={row.original?.image_assign_status}
              />
            )}
          </>
        ),
      },
      {
        id: "export_workflow",
        header: () => (
          <>
            <span>Export</span>
            <p className="extra">produkt na kanale</p>
          </>
        ),
        cell: ({ row }) => (
          <>
            {(row.depth === 0 || row.depth === 1) && (
              <ProgressBar data={row.original?.export_workflow_statuses} />
            )}
            {row.depth === 2 && (
              <Events
                is_mass_event_enabled={is_mass_event_enabled}
                mass_events={mass_events}
                setMassEvents={setMassEvents}
                refetch={() => refetch(row?.original?.product_group_id)}
                refetchStatuses={refetchStatuses}
                available_workflow="export_workflow"
                available_events={row.original?.available_events}
                id={row.original?.id}
                type={row.original?.type}
                status={row.original?.export_workflow_status}
              />
            )}
          </>
        ),
      },
    ],
    // eslint-disable-next-line
    [data, is_mass_event_enabled, mass_events],
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      expanded,
    },
    onExpandedChange: setExpanded,
    getSubRows: (row) => {
      if (row.channel_product_groups || row.products) {
        if (progress_status === "specification" || progress_status === "images") {
          return [...(row.products || [])].sort((a, b) =>
            a.sku.localeCompare(b.sku, undefined, {
              numeric: true,
              sensitivity: "base",
            }),
          );
        }

        if (progress_status === "category") {
          return [...(row.channel_product_groups || [])].sort(
            (a, b) => a.channel_id - b.channel_id,
          );
        }

        return [
          ...(row.channel_product_groups || []).sort(
            (a, b) => a.channel_id - b.channel_id,
          ),
          ...(row.products || []).sort((a, b) =>
            a.sku.localeCompare(b.sku, undefined, {
              numeric: true,
              sensitivity: "base",
            }),
          ),
        ];
      }

      return (row.channel_products || []).sort(
        (a, b) => a.channel_id - b.channel_id,
      );
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const main_filters = useMemo(
    () =>
      [
        {
          type: "input",
          label: "SKU produktu",
          name: "product_sku",
        },
        {
          type: "input",
          label: "SKU produktu grupowego",
          name: "product_group_sku",
        },
        {
          type: "input",
          label: "Nazwa produktu",
          name: "product_name",
        },
        {
          type: "multi-select",
          label: "Kategoria",
          name: "root_taxon_ids",
          options: root_taxons.map(({ id, identifier, path_names }) => ({
            label: getRootTaxonLabel({ identifier, path_names }),
            value: id,
          })),
        },
        {
          type: "multi-select",
          label: "Platforma",
          name: "platform_ids",
          options: platforms.map(({ id, name }) => ({
            label: name,
            value: id,
          })),
        },
        {
          type: "multi-select",
          label: "Sklep",
          name: "shop_ids",
          options: shops.map(({ id, name }) => ({
            label: name,
            value: id,
          })),
        },
        {
          type: "multi-select",
          label: "Kanał",
          name: "channel_ids",
          options: channels.map(({ id, identifier }) => ({
            label: identifier,
            value: id,
          })),
        },
        current_user.permission_workflow_admin_view && {
          type: "multi-select",
          label: "Pracownik",
          name: "user_ids",
          options: workers.map(({ id, full_name }) => ({
            label: full_name,
            value: id,
          })),
        },
      ].filter(Boolean),
    [
      root_taxons,
      platforms,
      shops,
      channels,
      workers,
      current_user.permission_workflow_admin_view,
      is_mass_event_enabled,
    ],
  );

  return (
    <div className="product-workflow-list">
      {!is_mass_event_enabled && (
        <MainFilters
          data={main_filters}
          current_filters={query_filters}
          setQueryFilters={handleSetQueryFilters}
        />
      )}
      <div className="product-workflow-list__table">
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{
                      ...getHeadStyle(header),
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={
                  `${row.original?.product_group_id}-${row.original?.id}` ||
                  `${row.id}-${row.index}`
                }
                {...{
                  onClick: row.getToggleExpandedHandler(),
                  style: getRowStyle(row),
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={`${cell.id}-${row.original?.id || row.index}`}
                    style={getCellStyle(cell)}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="product-workflow-list__footer" ref={load_more_ref}>
        {meta.has_next_page && (
          <button
            className="btn btn-lg btn-blue"
            type="button"
            onClick={fetchNextPage}
            disabled={is_fetching_next_page || is_refetching}
          >
            {is_fetching_next_page ? (
              <span>Proszę czekać...</span>
            ) : (
              <>
                <svg
                  className="m-r-10"
                  width="10"
                  height="6"
                  viewBox="0 0 10 6"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill="none"
                    d="M9 1L5 5L1 1"
                    stroke="#fff"
                    strokeLinecap="round"
                    strokeWidth="2"
                  />
                </svg>
                <span>Pokaż więcej</span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

export default connect(({ current_user }) => ({ current_user }))(
  GroupedProductsList,
);
